import axios, {CancelTokenSource} from 'axios'
import {DateTime} from 'luxon'
import {Buffer} from 'buffer'
import {fileTypeFromBuffer} from 'file-type'
import history from '../@f_routeshere/history'
import {SvgListProps} from '../props_reusable'
import {
  BookingWindowResponseProps,
  DateTimeSlotProps,
  TimeSlot,
} from '../props_reusable/visit'

interface OrderGUIDToken {
  [key: string]: {token: string; timeStamp: number}
}

/* FUNCTIONS
========================== */
function getSvgFromAListArr(SvgListArr: SvgListProps, iconType: string) {
  return Object.getOwnPropertyDescriptor(SvgListArr, iconType)?.value
}

const isWhiteListedPage = (path?: string): boolean => {
  const page = path || window.location.href
  const pages = ['/login', '/my-orders']
  return pages.some(s => page.includes(s))
}

const generateCancelTokenSource = (): CancelTokenSource => {
  const {CancelToken} = axios
  return CancelToken.source()
}

const isObjectEmpty = (obj: object) => Object.keys(obj).length === 0

const formatDate = (date: string, options: Intl.DateTimeFormatOptions) =>
  new Date(date).toLocaleDateString(navigator.language, options)

const isVisitUndated = (date = '') =>
  new Date(date).toLocaleDateString('en-US') === '12/31/9999'

const getErrorMsg = (labels: {[key: string]: string}, errorCode: string) =>
  errorCode in labels ? labels[errorCode] : labels['500']

const formatTime = (time: string) => {
  // eslint-disable-next-line
  const t = ~~time.split(':')[0]
  return t > 12 ? `${t - 12}pm` : `${t}am`
}

const getStartTimeAndEndTime = (
  data: BookingWindowResponseProps,
  slotId: number,
  slotGroupId: number,
): Pick<TimeSlot, 'startTime' | 'endTime'> => {
  const slotGroup = data.timeSlotGroups.find(t => t.id === slotGroupId)
  if (!slotGroup) return {startTime: '', endTime: ''}
  const matchedSlot = slotGroup.timeSlots.find(t => t.id === slotId)
  if (!matchedSlot) return {startTime: '', endTime: ''}
  const startTime = formatTime(matchedSlot.startTime)
  const endTime = formatTime(matchedSlot.endTime)
  return {
    startTime,
    endTime,
  }
}

const formatDateTimeSlots = (data: BookingWindowResponseProps) =>
  data.deliveryDays.reduce((acc: DateTimeSlotProps[], d) => {
    const list = d.bookingWindows.map(t => {
      const {timeSlotId, timeSlotGroupId, ecoSlot, availableToBook} = t
      const {startTime, endTime} = getStartTimeAndEndTime(
        data,
        timeSlotId,
        timeSlotGroupId,
      )
      return {
        date: d.date,
        timeSlotId,
        timeSlotGroupId,
        startTime,
        endTime,
        eco: ecoSlot,
        availableToBook,
      }
    })
    return [...acc, ...list] as DateTimeSlotProps[]
  }, [])

const getDateSlots = (
  date: string,
  data: DateTimeSlotProps[],
  filterOnlyAvailable = false,
) => {
  const dateSlots = data.filter(d => {
    const formatted = DateTime.fromISO(d.date).toFormat('LL/dd/yyyy')
    return filterOnlyAvailable
      ? formatted === date && d.availableToBook
      : formatted === date
  })
  return dateSlots
}

const getDateSlotType = (
  date: string,
  data: DateTimeSlotProps[],
): 'disabled' | 'echo' | 'selectable' => {
  const dateSlots = getDateSlots(date, data, true)
  if (!dateSlots.length) return 'disabled'
  const isEcoSlot = dateSlots.some(d => d.eco === true)
  return isEcoSlot ? 'echo' : 'selectable'
}

const getParamsFromURL = (key: string) => {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get(key) || ''
}

const routeToErrorPage = (err: Error) => {
  const statusCode = err.message
  const path = statusCode === '404' ? '/notFound' : '/500'
  if (
    !window.location.href.includes(path) &&
    !window.location.href.includes('notFound')
  ) {
    history.push(path)
  }
}

const getTokenBasedOnOrderGUID = (orderGUID: string) => {
  const tokens: OrderGUIDToken = JSON.parse(
    localStorage.getItem('tokens') || '{}',
  )
  return tokens[orderGUID]?.token || ''
}

const setTokenInStorage = (orderGUID: string, token: string) => {
  const tokens: OrderGUIDToken = JSON.parse(
    localStorage.getItem('tokens') || '{}',
  )
  tokens[orderGUID] = {token, timeStamp: new Date().getTime()}
  localStorage.setItem('tokens', JSON.stringify(tokens))
}

const removeTokenFromStorage = (orderGUID: string) => {
  const tokens: OrderGUIDToken = JSON.parse(
    localStorage.getItem('tokens') || '{}',
  )
  delete tokens[orderGUID]
  localStorage.setItem('tokens', JSON.stringify(tokens))
}

const removeExpiryToken = () => {
  const tokens: OrderGUIDToken = JSON.parse(
    localStorage.getItem('tokens') || '{}',
  )
  const tokenCopy = JSON.parse(JSON.stringify(tokens))
  const now = new Date()
  const expiryTime = 24 * 60 * 60 * 1000 // One day for expiration
  // eslint-disable-next-line
  for (const [orderGUID, {timeStamp}] of Object.entries(tokens)) {
    if (now.getTime() - timeStamp > expiryTime) {
      delete tokenCopy[orderGUID]
    }
  }
  localStorage.setItem('tokens', JSON.stringify(tokenCopy))
}

const generateBase64EncodedURL = (base64Str: string): Promise<string> => {
  const base64string_buffer = Buffer.from(base64Str, 'base64')
  return fileTypeFromBuffer(base64string_buffer).then(result => {
    const mimeType = result?.mime ? result?.mime : 'image/svg+xml'
    return `data:${mimeType};base64,${base64string_buffer.toString('base64')}`
  })
}

const classnames = (obj: {[key: string]: boolean}): string => {
  const classes = []
  // eslint-disable-next-line
  for (const [key, value] of Object.entries(obj)) {
    if (value) classes.push(key)
  }
  return classes.join(' ')
}

const stringToUri = (s: string) => s.toLowerCase().replace(/ +/g, '-')

const isQtySingular = (qty: number) => qty === 1

const isQtyPlural = (qty: number) => qty > 1

const toggleRoot = (opacity: string) => {
  const element: HTMLDivElement = document.getElementById(
    'root',
  ) as HTMLDivElement
  element.style.opacity = opacity
}

export default {
  getSvgFromAListArr,
  formatDate,
  isObjectEmpty,
  getErrorMsg,
  getParamsFromURL,
  routeToErrorPage,
  setTokenInStorage,
  removeTokenFromStorage,
  removeExpiryToken,
  getTokenBasedOnOrderGUID,
  formatDateTimeSlots,
  getDateSlots,
  getDateSlotType,
  generateBase64EncodedURL,
  classnames,
  isWhiteListedPage,
  generateCancelTokenSource,
  formatTime,
  isVisitUndated,
  stringToUri,
  isQtySingular,
  isQtyPlural,
  toggleRoot,
}
